import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Topbar from '../PulseSurvey/Topbar';
import Loader from '../../component/common/Loader';
import BasicInfoForm from '../../component/HealthComponent/HealthScheme/BasicInfoForm';
import PlanManagement from '../../component/HealthComponent/HealthScheme/PlanManagement';
import ConfirmPopup from '../../component/common/modals/ConfirmPopup';
import { CompanyApi } from '../../api/companyApi';
import { Api } from '../../api';
import { isSuperAndSubAdmin, errorToast, successToast } from '../../utils/helper';
import { is } from 'date-fns/locale';

const initialFormData = {
  companyId: '',
  providerName: '',
  providerContact: '',
  providerEmail: '',
  providerWebsite: '',
  startDate: '',
  expiryDate: '',
  billingAmount: 0,
  healthCashPlans: [],
  occupationalHealthPlans: []
};

const AddEditHealthScheme = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const editId = queryParams.get('id');
  const isEdit = Boolean(editId);
  const isSuperOrSubAdmin = isSuperAndSubAdmin();
  const companyId = localStorage.getItem('userData')
    ? JSON.parse(localStorage.getItem('userData')).companyId
    : '';
  const userData = localStorage.getItem('userData')
    ? JSON.parse(localStorage.getItem('userData'))
    : null;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [hasExistingScheme, setHasExistingScheme] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [existingSchemeId, setExistingSchemeId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Company selection states (for super admin and sub admin)
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companySearchText, setCompanySearchText] = useState('');
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyCurrentPage, setCompanyCurrentPage] = useState(1);
  const [companyHasMore, setCompanyHasMore] = useState(true);
  const companyDropdownRef = useRef(null);

  // Load health scheme on page load
  useEffect(() => {
    if (editId) {
      // If editId exists in query params, fetch scheme by ID
      loadHealthSchemeById(editId);
    } else if (isSuperOrSubAdmin) {
      // Fetch companies for super admin and sub admin
      fetchCompanies(1, '', false);
    } else {
      // For company admin, load their scheme directly
      loadHealthSchemeData();
    }
  }, []);

  // Handle company search with debounce
  useEffect(() => {
    if (!isSuperOrSubAdmin) return;

    const delayDebounceFn = setTimeout(() => {
      if (isCompanyDropdownOpen) {
        fetchCompanies(1, companySearchText, false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [companySearchText]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isSuperOrSubAdmin) return;

    const handleClickOutside = (event) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setIsCompanyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCompanies = async (page = 1, searchKey = '', isAppending = false) => {
    if (companyLoading) return;

    setCompanyLoading(true);
    try {
      const response = await Api.getAllCompanies(page, 10, searchKey, 'companyName', 1);

      if (response?.data?.meta?.code === 1) {
        const newCompanies = response.data.data || [];
        const totalCount = response.data.meta?.totalRecords || 0;

        if (isAppending) {
          setCompanies((prev) => [...prev, ...newCompanies]);
        } else {
          setCompanies(newCompanies);
        }

        setCompanyCurrentPage(page);
        setCompanyHasMore(
          newCompanies.length == 0 ? false : companies.length + newCompanies.length < totalCount
        );
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setCompanyLoading(false);
    }
  };

  const handleCompanySelect = async (company) => {
    setSelectedCompany(company);
    setIsCompanyDropdownOpen(false);
    setCompanySearchText('');
    setFormData((prev) => ({ ...prev, companyId: company._id }));

    // Clear company error if exists
    if (errors.companyId) {
      setErrors((prev) => ({ ...prev, companyId: '' }));
    }

    // Load health scheme for selected company
    await loadHealthSchemeData(company._id);
  };

  const handleCompanySearch = (e) => {
    setCompanySearchText(e.target.value);
    setCompanyCurrentPage(1);
  };

  const handleCompanyScroll = useCallback(
    (e) => {
      const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
      if (bottom && companyHasMore && !companyLoading) {
        fetchCompanies(companyCurrentPage + 1, companySearchText, true);
      }
    },
    [companyHasMore, companyLoading, companyCurrentPage, companySearchText]
  );

  const loadHealthSchemeById = async (companyId) => {
    setLoading(true);
    try {
      // Fetch scheme by ID based on user role
      const response = isSuperOrSubAdmin
        ? await Api.getActiveHealthSchemeByCompId(companyId)
        : await CompanyApi.getHealthScheme();

      console.log('Health Scheme By ID Response:', response.data);

      if (response?.data?.meta?.code === 1) {
        const schemeData = response.data.data;

        if (schemeData) {
          setHasExistingScheme(true);
          setExistingSchemeId(schemeData._id || schemeData.schemeId);
          setIsEditMode(false); // Start in view mode for company admin

          // If super/sub admin, set the selected company
          if (isSuperOrSubAdmin && schemeData.companyId) {
            // Fetch company details to set selectedCompany
            const companyResponse = await Api.getAllCompanies(1, 10, '', 'companyName', 1);
            if (companyResponse?.data?.meta?.code === 1) {
              const company = companyResponse.data.data.find((c) => c._id === schemeData.companyId);
              if (company) {
                setSelectedCompany(company);
              }
            }
          }

          setFormData({
            companyId: schemeData.companyId || '',
            providerName: schemeData.providerName || '',
            providerContact: schemeData.providerContact || '',
            providerEmail: schemeData.providerEmail || '',
            providerWebsite: schemeData.providerWebsite || '',
            startDate: schemeData.startDate || '',
            expiryDate: schemeData.expiryDate || '',
            billingAmount: schemeData.billingAmount || 0,
            healthCashPlans:
              schemeData.healthCashPlans?.map((hc) => ({ ...hc, planName: hc.title })) || [],
            occupationalHealthPlans:
              schemeData.occupationalHealthPlans?.map((hc) => ({ ...hc, planName: hc.title })) || []
          });
        } else {
          toast.error('Health scheme not found');
          navigate('/health-scheme');
        }
      } else {
        toast.error(response?.data?.meta?.message || 'Failed to load health scheme');
        navigate('/health-scheme');
      }
    } catch (error) {
      console.error('Error loading health scheme by ID:', error);
      toast.error('Failed to load health scheme');
      navigate('/health-scheme');
    } finally {
      setLoading(false);
    }
  };

  const loadHealthSchemeData = async (selectedCompanyId = null, isEditMode = false) => {
    // For super/sub admin, require company selection first
    if (isSuperOrSubAdmin && !selectedCompanyId) {
      return;
    }

    setLoading(true);
    try {
      // Get existing health scheme data
      const response = isSuperOrSubAdmin
        ? await Api.getActiveHealthSchemeByCompId(selectedCompanyId)
        : await CompanyApi.getHealthScheme(companyId);
      console.log('Health Scheme Response:', response.data);
      if (response?.data?.meta?.code == 1) {
        const schemeData = response.data.data;

        // Check if scheme data exists and has required fields
        if (schemeData && (schemeData.schemeId || schemeData._id)) {
          setHasExistingScheme(true);
          setExistingSchemeId(schemeData.schemeId || schemeData._id);
          setIsEditMode(false); // Start in view mode
          setFormData({
            companyId: selectedCompanyId || schemeData.companyId || '',
            providerName: schemeData.providerName || '',
            providerContact: schemeData.providerContact || '',
            providerEmail: schemeData.providerEmail || '',
            providerWebsite: schemeData.providerWebsite || '',
            startDate: schemeData.startDate || '',
            expiryDate: schemeData.expiryDate || '',
            billingAmount: schemeData.billingAmount || 0,
            healthCashPlans:
              schemeData.healthCashPlans?.map((hc) => ({ ...hc, planName: hc.title })) || [],
            occupationalHealthPlans:
              schemeData.occupationalHealthPlans?.map((hc) => ({ ...hc, planName: hc.title })) || []
          });
        } else {
          // No existing scheme, show create form
          setHasExistingScheme(false);
          setIsEditMode(true); // Allow editing for new scheme
          if (selectedCompanyId) {
            setFormData((prev) => ({ ...prev, companyId: selectedCompanyId }));
          }
        }
      } else {
        // No existing scheme found (could be 404 or no data)
        setHasExistingScheme(false);
        setIsEditMode(true); // Allow editing for new scheme
        if (selectedCompanyId) {
          setFormData((prev) => ({ ...prev, companyId: selectedCompanyId }));
        }
      }
    } catch (error) {
      console.error('Error loading health scheme:', error);
      // If error (like 404), assume no scheme exists
      setHasExistingScheme(false);
      setIsEditMode(true); // Allow editing for new scheme
    } finally {
      setLoading(false);
    }
  };

  const handleFormDataChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleHealthCashPlansChange = (plans) => {
    setFormData((prev) => ({
      ...prev,
      healthCashPlans: plans
    }));
  };

  const handleOccupationalPlansChange = (plans) => {
    setFormData((prev) => ({
      ...prev,
      occupationalHealthPlans: plans
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Company validation for super admin and sub admin
    if (isSuperOrSubAdmin && !formData.companyId) {
      newErrors.companyId = 'Company is required';
    }

    // Required field validations
    if (!formData.providerName?.trim()) {
      newErrors.providerName = 'Provider name is required';
    }

    if (!formData.providerContact?.trim()) {
      newErrors.providerContact = 'Provider contact is required';
    }
    if (formData.providerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.providerEmail)) {
      newErrors.providerEmail = 'Please enter a valid email address';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.expiryDate) {
      newErrors.expiryDate = 'End date is required';
    }

    if (
      formData.startDate &&
      formData.expiryDate &&
      new Date(formData.startDate) >= new Date(formData.expiryDate)
    ) {
      newErrors.expiryDate = 'End date must be after start date';
    }

    if (!formData.billingAmount || formData.billingAmount <= 0) {
      newErrors.billingAmount = 'Billing amount must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setLoading(true);
    try {
      // Prepare payload according to the expected format
      const payload = {
        ...formData,
        // Include companyId for super admin and sub admin
        ...(isSuperOrSubAdmin ? { companyId: formData.companyId } : { companyId: companyId }),
        isCustomPlan: true,
        // Convert arrays to the expected format if needed
        healthCashPlans: formData.healthCashPlans.map((plan) => ({
          planName: plan.planName,
          description: plan.description,
          points: plan.points?.filter((point) => point?.trim()) || [],
          contact: plan.contact || '',
          info: plan.info || ''
        })),
        occupationalHealthPlans: formData.occupationalHealthPlans.map((plan) => ({
          planName: plan.planName,
          description: plan.description,
          points: plan.points?.filter((point) => point?.trim()) || [],
          contact: plan.contact || '',
          info: plan.info || ''
        }))
      };

      // Call the appropriate API based on user role and mode
      let response;
      if (isSuperOrSubAdmin) {
        // Admin users: Use separate create/update methods
        if (hasExistingScheme && existingSchemeId) {
          response = await Api.UpdateCustomHealthScheme(existingSchemeId, payload);
        } else {
          response = await Api.createCustomHealthScheme(payload);
        }
      } else {
        // Company users: Use combined method (includes id in payload for update)
        const companyPayload = {
          ...payload,
          ...(hasExistingScheme && existingSchemeId && { id: existingSchemeId })
        };
        response = await CompanyApi.addEditHealthScheme(companyPayload);
      }

      if (response?.data?.meta?.code === 1) {
        toast.success(
          hasExistingScheme
            ? 'Health scheme updated successfully'
            : 'Health scheme created successfully'
        );
        navigate(isSuperOrSubAdmin ? '/health-scheme' : `/${userData.slug}/health-scheme`);
        setIsEditMode(false); // Switch back to view mode
      } else {
        toast.error(response?.data?.meta?.message || 'Failed to save health scheme');
      }
    } catch (error) {
      console.error('Error saving health scheme:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
    if (isEditMode) {
      // If canceling edit, reload original data
      loadHealthSchemeData(isSuperOrSubAdmin ? formData.companyId : null);
    }
  };

  const handlePreviewToggle = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const confirmDelete = async () => {
    if (!existingSchemeId) return;

    setIsDeleting(true);
    try {
      const response = isSuperOrSubAdmin
        ? await Api.deleteHealthScheme(existingSchemeId)
        : await CompanyApi.deleteHealthScheme(existingSchemeId);

      if (response?.data?.meta?.code === 1) {
        successToast('Health scheme deleted successfully');
        setShowDeleteConfirm(false);
        // Navigate back to health scheme page
        if (isSuperOrSubAdmin) {
          navigate('/health-scheme');
        } else {
          navigate(`/${userData.slug}/health-scheme`);
        }
      } else {
        errorToast(response?.data?.meta?.message || 'Failed to delete scheme');
      }
    } catch (error) {
      console.error('Error deleting scheme:', error);
      errorToast('Failed to delete scheme');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="relative">
      {loading && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          {hasExistingScheme
            ? isEditMode
              ? 'Edit Health Scheme'
              : 'View Health Scheme'
            : 'Create Health Scheme'}{' '}
          - Shoorah
        </title>
      </Helmet>

      <Topbar
        title={
          hasExistingScheme
            ? isEditMode
              ? 'Edit Health Scheme'
              : 'View Health Scheme'
            : 'Create Health Scheme'
        }
        hasDesc={true}
        description="Create and manage comprehensive health schemes with cash plans and occupational health coverage"
        infoBtn={false}
      />

      <div className="flex flex-col w-full max-w-7xl mt-8 space-y-6">
        {/* Header */}
        <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                {hasExistingScheme
                  ? isEditMode
                    ? 'Edit Health Scheme'
                    : 'View Health Scheme'
                  : 'Create New Health Scheme'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                {hasExistingScheme
                  ? isEditMode
                    ? 'Update your existing health scheme details'
                    : 'Review your current health scheme configuration'
                  : 'Set up a comprehensive health coverage plan'}
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Edit/Cancel Button for existing scheme */}
              {hasExistingScheme && (
                <button
                  onClick={handleEditToggle}
                  className={isEditMode ? 'btn-secondary' : 'btn-primary'}
                >
                  {isEditMode ? 'Cancel Edit' : 'Edit Scheme'}
                </button>
              )}

              {/* Delete Button - Only show in view mode */}
              {hasExistingScheme && !isEditMode && (
                <button
                  onClick={handleDelete}
                  className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Delete Scheme
                </button>
              )}

              {/* Preview Button */}
              {/* {isEditMode && (
                <button
                  onClick={handlePreviewToggle}
                  className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {isPreviewMode ? 'Edit Mode' : 'Preview'}
                </button>
              )} */}
            </div>
          </div>
        </div>
        {/* Company Selection Section - Only for Super Admin and Sub Admin */}
        {isSuperOrSubAdmin && (
          <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Select Company
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Choose the company for this health scheme
                </p>
              </div>

              {/* Company Dropdown */}
              <div className="relative w-full sm:w-96" ref={companyDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsCompanyDropdownOpen(!isCompanyDropdownOpen)}
                  disabled={hasExistingScheme && !isEditMode}
                  className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 transition-colors ${
                    errors.companyId
                      ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-2 focus:ring-shoorah-primary'
                  } ${
                    hasExistingScheme && !isEditMode
                      ? 'cursor-not-allowed opacity-60'
                      : 'hover:border-gray-400 cursor-pointer'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-blue-600 dark:text-blue-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    </div>
                    <div className="text-left flex-1 min-w-0">
                      {selectedCompany ? (
                        <>
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {selectedCompany.company_name}
                          </p>
                          {/* <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {selectedCompany.company_email || 'No email'}
                          </p> */}
                        </>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Select a company...
                        </p>
                      )}
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      isCompanyDropdownOpen ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {errors.companyId && (
                  <p className="text-red-500 text-xs mt-1">{errors.companyId}</p>
                )}

                {/* Dropdown Menu */}
                {isCompanyDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-80 flex flex-col">
                    {/* Search Input */}
                    <div className="p-3 border-b border-gray-200 dark:border-gray-600">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search companies..."
                          value={companySearchText}
                          onChange={handleCompanySearch}
                          className="w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-shoorah-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>

                    {/* Company List */}
                    <div className="overflow-y-auto flex-1" onScroll={handleCompanyScroll}>
                      {companyLoading && companies.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                          Loading companies...
                        </div>
                      ) : companies.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                          No companies found
                        </div>
                      ) : (
                        <>
                          {companies.map((company) => (
                            <button
                              key={company._id}
                              type="button"
                              onClick={() => handleCompanySelect(company)}
                              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                                selectedCompany?._id === company._id
                                  ? 'bg-blue-50 dark:bg-blue-900/20'
                                  : ''
                              }`}
                            >
                              <div className="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                                {company.company_logo ? (
                                  <img
                                    src={company.company_logo}
                                    alt={`${company.company_name} logo`}
                                    className="w-10 h-10 rounded-full object-cover"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = `<svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>`;
                                    }}
                                  />
                                ) : (
                                  <svg
                                    className="w-5 h-5 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                    />
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1 text-left min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {company.company_name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {company.company_email || 'No email'}
                                </p>
                              </div>
                              {selectedCompany?._id === company._id && (
                                <svg
                                  className="w-5 h-5 text-shoorah-primary flex-shrink-0"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </button>
                          ))}
                          {companyLoading && (
                            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                              Loading more...
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {!isPreviewMode ? (
          <>
            {/* Basic Information Form */}
            <BasicInfoForm
              formData={formData}
              errors={errors}
              onChange={handleFormDataChange}
              disabled={hasExistingScheme && !isEditMode}
            />

            {/* Health Cash Plans */}
            <PlanManagement
              title="Health Cash Plans"
              plans={formData.healthCashPlans}
              onChange={handleHealthCashPlansChange}
              disabled={hasExistingScheme && !isEditMode}
              placeholder="No health cash plans added yet. These plans typically cover dental, optical, and other healthcare expenses."
            />

            {/* Occupational Health Plans */}
            <PlanManagement
              title="Occupational Health Plans"
              plans={formData.occupationalHealthPlans}
              onChange={handleOccupationalPlansChange}
              disabled={hasExistingScheme && !isEditMode}
              placeholder="No occupational health plans added yet. These plans focus on workplace health and safety."
            />

            {/* Action Buttons - only show in edit mode */}
            {isEditMode && (
              <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <p>
                      <span className="font-medium">Total Plans:</span>{' '}
                      {formData.healthCashPlans.length + formData.occupationalHealthPlans.length}
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={hasExistingScheme ? handleEditToggle : () => navigate(-1)}
                      className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="btn-primary"
                    >
                      {loading
                        ? hasExistingScheme
                          ? 'Updating...'
                          : 'Creating...'
                        : hasExistingScheme
                        ? 'Update Health Scheme'
                        : 'Create Health Scheme'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Preview Mode */}
            <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Health Scheme Preview
                </h2>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-shoorah-primary">
                      £{formData.billingAmount}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Billing Amount</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-shoorah-primary">
                      {formData.healthCashPlans.length}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Cash Plans</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-shoorah-primary">
                      {formData.occupationalHealthPlans.length}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Health Plans</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-shoorah-primary">
                      {Math.ceil(
                        (new Date(formData.expiryDate) - new Date(formData.startDate)) /
                          (1000 * 60 * 60 * 24)
                      )}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Days Coverage</p>
                  </div>
                </div>
              </div>

              {/* Provider Information */}
              <div className="border-t pt-6 mb-8">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Provider Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <p>
                    <span className="font-medium">Provider:</span> {formData.providerName}
                  </p>
                  <p>
                    <span className="font-medium">Contact:</span> {formData.providerContact}
                  </p>
                  <p>
                    <span className="font-medium">Email:</span> {formData.providerEmail}
                  </p>
                  {formData.providerWebsite && (
                    <p>
                      <span className="font-medium">Website:</span>
                      <a
                        href={formData.providerWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-shoorah-primary hover:underline ml-1"
                      >
                        {formData.providerWebsite}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Preview Plans */}
            {formData.healthCashPlans.length > 0 && (
              <PlanManagement
                title="Health Cash Plans"
                plans={formData.healthCashPlans}
                onChange={() => {}}
                disabled={true}
              />
            )}

            {formData.occupationalHealthPlans.length > 0 && (
              <PlanManagement
                title="Occupational Health Plans"
                plans={formData.occupationalHealthPlans}
                onChange={() => {}}
                disabled={true}
              />
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Popup */}
      <ConfirmPopup
        open={showDeleteConfirm}
        setOpen={setShowDeleteConfirm}
        setAccepted={confirmDelete}
        handleNo={handleDeleteCancel}
        message="Are you sure you want to delete this health scheme? This action cannot be undone and will remove all associated data."
        isLoading={isDeleting}
      />
    </div>
  );
};

export default AddEditHealthScheme;
